import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { AnalyticsConfig, DATADOG_SERVICE, DATADOG_SITE } from './analytics-config';

export type AnalyticsProperties = Record<string, string | number | boolean>;

export interface HeapApi {
  identify(identity: string): void;
  addUserProperties(properties: AnalyticsProperties): void;
  addAccountProperties(properties: AnalyticsProperties): void;
  track(eventName: string, properties?: AnalyticsProperties): void;
}

export interface AppcuesApi {
  identify(userId: string, properties?: AnalyticsProperties): void;
  group(groupId: string, properties?: AnalyticsProperties): void;
}

declare global {
  interface Window {
    AppcuesSettings?: { enableURLDetection: boolean };
    Appcues?: AppcuesApi;
    heap?: HeapApi;
    heapReadyCb?: { name: string; fn: () => void }[];
  }
}

/**
 * Called from main.ts before bootstrap so RUM captures the initial load. Session replay starts
 * recording automatically for sampled sessions.
 */
export function initDatadogRum(config: AnalyticsConfig | null) {
  const datadog = config?.datadog;
  if (!datadog) {
    return;
  }

  datadogRum.init({
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: datadog.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: datadog.sessionReplaySampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

// Methods the Heap loader stubs until heap_config.js replaces window.heap with the real SDK.
const HEAP_STUB_METHODS = [
  'init',
  'startTracking',
  'stopTracking',
  'track',
  'resetIdentity',
  'identify',
  'identifyHashed',
  'getSessionId',
  'getUserId',
  'getIdentity',
  'addUserProperties',
  'addEventProperties',
  'removeEventProperty',
  'clearEventProperties',
  'addAccountProperties',
  'addAdapter',
  'addTransformer',
  'addTransformerFn',
  'onReady',
  'addPageviewProperties',
  'removePageviewProperty',
  'clearPageviewProperties',
  'trackPageview',
];

/**
 * Script loaders for the vendor SDKs, wrapped in a service so TrackingService can be tested
 * without injecting real <script> tags.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsVendors {
  readonly rum = datadogRum;

  /**
   * TypeScript port of Heap's loader snippet. The returned stub queues calls into heapReadyCb and
   * the SDK replays them once it loads, so identify() is never lost to a slow script.
   */
  loadHeap(appId: string): HeapApi {
    if (window.heap && document.getElementById('heap-loader')) {
      return window.heap;
    }

    const readyCallbacks = (window.heapReadyCb = window.heapReadyCb || []);
    // Heap's snippet uses an array as the stub; keep that shape for the SDK.
    const stub = [] as unknown as Record<string, unknown>;
    stub['envId'] = appId;
    stub['clientConfig'] = { shouldFetchServerConfig: false };
    for (const name of HEAP_STUB_METHODS) {
      stub[name] = (...args: unknown[]) =>
        readyCallbacks.push({
          name,
          fn: () => {
            const heap = window.heap as unknown as Record<string, (...a: unknown[]) => void>;
            heap[name]?.call(heap, ...args);
          },
        });
    }
    window.heap = stub as unknown as HeapApi;

    const script = document.createElement('script');
    script.id = 'heap-loader';
    script.async = true;
    script.src = `https://cdn.us.heap-api.com/config/${encodeURIComponent(appId)}/heap_config.js`;
    document.head.appendChild(script);

    return window.heap;
  }

  /**
   * Appcues has no call-queue stub, so calls made before the SDK loads would be dropped. Resolve
   * with the SDK once the script has loaded instead. URL detection makes Appcues fire page()
   * itself on every SPA route change.
   */
  loadAppcues(accountId: string): Promise<AppcuesApi> {
    window.AppcuesSettings = { enableURLDetection: true };

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'appcues-sdk';
      script.async = true;
      script.src = `https://fast.appcues.com/${encodeURIComponent(accountId)}.js`;
      script.onload = () =>
        window.Appcues
          ? resolve(window.Appcues)
          : reject(new Error('Appcues SDK loaded without window.Appcues'));
      script.onerror = () => reject(new Error('Appcues SDK failed to load'));
      document.head.appendChild(script);
    });
  }
}
